import type { ReactNode } from 'react';
import { useAuth } from '@/hooks/auth/useAuth';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';

interface RequireSuperAdminProps {
  children: ReactNode;
}

/**
 * Route/content guard that renders its children only when the current user is
 * a platform admin (super admin OR the assignable `platform-admin` role).
 *
 * Bypass rules:
 *  - Auth disabled (single-tenant default) → always allow.
 *  - Platform admin → always allow.
 *
 * The component name is preserved for backwards compatibility with the many
 * existing callers; consider {@link RequirePlatformAdmin} (alias below) when
 * writing new code so the gate's behaviour is self-documenting.
 */
export function RequireSuperAdmin({ children }: RequireSuperAdminProps) {
  const { isPlatformAdmin, authEnabled, loading } = useAuth();
  if (loading) return null;
  if (authEnabled && !isPlatformAdmin) {
    return (
      <PageLayout>
        <PageHeader
          title="Access denied"
          description="You do not have permission to view this page."
        />
      </PageLayout>
    );
  }
  return <>{children}</>;
}

/**
 * Self-documenting alias for {@link RequireSuperAdmin}. Use this name in new
 * code so the intent (gate on `isSuperAdmin || isAppAdmin`) is explicit.
 */
export const RequirePlatformAdmin = RequireSuperAdmin;
